import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, tap } from 'rxjs';
import { ApiConfigService } from '../api/api-config.service';

/**
 * Contrato real: GET /api/login/permissions/effective — camelCase, como toda
 * a API (confirmado via DevTools em produção, 2026-10-13, front#195:
 * `{"permissoes":[],"isMaster":true,"isDiretor":false}`). Uma versão
 * anterior deste arquivo assumia PascalCase e nunca aplicava permissão
 * alguma — ver front#195 para o incidente.
 */
interface PermissionsEffectiveResponse {
  permissoes: string[];
  isMaster: boolean;
  isDiretor: boolean;
}

const EMPTY_RESPONSE: PermissionsEffectiveResponse = {
  permissoes: [],
  isMaster: false,
  isDiretor: false,
};

/**
 * Permissões efetivas do usuário no tenant ativo (PBAC).
 * Deve ser recarregado após login e a cada troca de tenant — ver
 * AppShellComponent (effect sobre TenantService.activeTenantId()).
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiConfigService);

  private readonly _permissions = signal<string[]>([]);
  private readonly _isMaster = signal(false);
  private readonly _isDiretor = signal(false);
  private readonly _loaded = signal(false);

  readonly permissions = this._permissions.asReadonly();
  readonly isMaster = this._isMaster.asReadonly();
  readonly isDiretor = this._isDiretor.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  /** Acesso irrestrito: Diretor do tenant ou Master administrando o tenant. */
  readonly hasFullAccess = computed(() => this._isDiretor() || this._isMaster());

  load(): Observable<PermissionsEffectiveResponse> {
    return this.http
      .get<PermissionsEffectiveResponse>(this.api.url('/api/login/permissions/effective'))
      .pipe(
        tap(res => this.apply(res)),
        catchError(() => {
          // Fallback permissivo (seção 2 do guia PBAC): falha ao carregar
          // permissões não deve travar a UI — trata como "sem permissões
          // específicas conhecidas", nunca desloga por isso.
          this.apply(EMPTY_RESPONSE);
          return of(EMPTY_RESPONSE);
        })
      );
  }

  /**
   * Verifica se o usuário tem a permissão informada ("modulo.recurso.acao").
   * isMaster/isDiretor liberam tudo. Suporta curinga de delegação "modulo.*".
   */
  can(permission: string): boolean {
    if (this.hasFullAccess()) return true;

    const perms = this._permissions();
    if (perms.includes(permission)) return true;

    const modulo = permission.split('.')[0];
    return perms.includes(`${modulo}.*`);
  }

  clear(): void {
    this._permissions.set([]);
    this._isMaster.set(false);
    this._isDiretor.set(false);
    this._loaded.set(false);
  }

  private apply(res: PermissionsEffectiveResponse): void {
    this._permissions.set(res?.permissoes ?? []);
    this._isMaster.set(!!res?.isMaster);
    this._isDiretor.set(!!res?.isDiretor);
    this._loaded.set(true);
  }
}
